import { useLayoutEffect, useRef } from 'react';
import { CheckCircle } from '@phosphor-icons/react/dist/ssr/CheckCircle';
import { Circle } from '@phosphor-icons/react/dist/ssr/Circle';
import { Plus } from '@phosphor-icons/react/dist/ssr/Plus';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import RoundAvatar from '../RoundAvatar';
import styles from './SheetOverlay.module.css';

/**
 * The rows a SheetOverlay holds (Drop 7 DETAIL §8). One control language: a
 * check (brand colour) when on, an empty circle when off — never a switch.
 */

const Check = ({ on }) =>
  on ? (
    <CheckCircle size={20} weight="fill" className={styles.ck} />
  ) : (
    <Circle size={20} className={styles.ck} />
  );

/**
 * A row you check — a group, a venue, a field you share.
 * @param {object} p
 * @param {string} p.label
 * @param {string} [p.sub] - the grey second line (a venue's type and address)
 * @param {React.ComponentType} [p.icon] - a Phosphor glyph on the left
 * @param {boolean} [p.indent] - no icon, but aligned with rows that have one
 * @param {boolean} [p.tall] - the 54px share-sheet row
 * @param {boolean} p.on
 * @param {Function} p.onToggle
 * @param {boolean} [p.hideCheck] - a row you pick rather than check (a contact type)
 */
export const SheetCheckRow = ({
  label,
  sub,
  icon: Icon,
  indent,
  tall,
  on,
  onToggle,
  hideCheck,
}) => (
  <button
    type="button"
    className={[styles.ovg, on && styles.on, tall && styles.tall, indent && styles.noicon]
      .filter(Boolean)
      .join(' ')}
    aria-pressed={hideCheck ? undefined : on}
    onClick={onToggle}
  >
    {Icon ? <Icon size={17} className={styles.g} /> : null}
    <span className={styles.v}>
      {label}
      {sub ? <span className={styles.sub}>{sub}</span> : null}
    </span>
    {hideCheck ? null : <Check on={on} />}
  </button>
);

/**
 * A row edited in place — a contact value with its glyph and the x.
 * @param {object} p
 * @param {React.ComponentType} [p.icon]
 * @param {string} p.value
 * @param {Function} p.onChange - (value)
 * @param {Function} [p.onClear] - the x; absent = no x
 * @param {string} [p.placeholder]
 * @param {Object} [p.hints] - the field's keyboard + autofill attributes,
 *   utils/contactFieldHints (one home): type, inputMode, autoComplete, name,
 *   autoCapitalize, autoCorrect, spellCheck. Omit: a plain text field
 * @param {Function} [p.onCommit] - return / blur
 */
export const SheetFieldRow = ({
  icon: Icon,
  value,
  onChange,
  onClear,
  placeholder,
  hints = {},
  onCommit,
  autoFocus,
}) => {
  const ref = useRef(null);
  // autoFocus from code with preventScroll — the native autoFocus scrolls the
  // page to the field (SheetOverlay's no-scroll focus, the v7976 jump)
  useLayoutEffect(() => {
    if (autoFocus) ref.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className={styles.ovr}>
      {Icon ? <Icon size={17} className={styles.g} /> : null}
      <input
        ref={ref}
        type={hints.type || 'text'}
        inputMode={hints.inputMode}
        autoComplete={hints.autoComplete}
        name={hints.name}
        autoCapitalize={hints.autoCapitalize}
        autoCorrect={hints.autoCorrect}
        spellCheck={hints.spellCheck}
        enterKeyHint="done"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onCommit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
      />
      {onClear ? (
        <button type="button" className={styles.x} aria-label="Remove" onClick={onClear}>
          <X size={15} />
        </button>
      ) : null}
    </div>
  );
};

/** A contact the peer shared — greyed, not editable, no x (DETAIL §8c). */
export const SheetGivenRow = ({ icon: Icon, value }) => (
  <div className={`${styles.ovr} ${styles.given}`}>
    {Icon ? <Icon size={17} className={styles.g} /> : null}
    <span className={styles.v}>{value}</span>
  </div>
);

/** A section label above a run of rows — `Shared`, `Added` (DETAIL §8c). */
export const SheetLabel = ({ children, more = false }) => (
  <div className={more ? `${styles.ovh} ${styles.more}` : styles.ovh}>{children}</div>
);

/** `＋ New group`, `＋ Add contact`. */
export const SheetAddRow = ({ label, onClick }) => (
  <button type="button" className={styles.ovadd} onClick={onClick}>
    <Plus size={15} />
    {label}
  </button>
);

/**
 * A field typed into the sheet (`＋ Venue or event`, a new group's name).
 * @param {object} p
 * @param {string} p.value
 * @param {Function} p.onChange - (value)
 * @param {Function} [p.onFocus]
 * @param {Function} [p.onSubmit] - return
 * @param {string} p.placeholder
 * @param {boolean} [p.typing] - the brand ring while it has focus
 */
export const SheetInputRow = ({
  value,
  onChange,
  onFocus,
  onBlur,
  onSubmit,
  placeholder,
  typing,
}) => (
  <div className={[styles.ovin, typing && styles.typing].filter(Boolean).join(' ')}>
    <Plus size={17} className={styles.g} />
    <input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSubmit?.();
      }}
    />
  </div>
);

/**
 * A full-width choose-one pill (DETAIL §8d "Where you met"): the current choice
 * is black, the rest white; a tap chooses. 16px apart.
 * @param {object} p
 * @param {React.ReactNode} p.label
 * @param {boolean} p.selected
 * @param {Function} p.onClick
 */
export const SheetPillRow = ({ label, selected, onClick }) => (
  <button
    type="button"
    className={[styles.ovpill, selected && styles.sel].filter(Boolean).join(' ')}
    aria-pressed={selected}
    onClick={onClick}
  >
    {label}
  </button>
);

/** `More nearby` — centred text + caret under the five venue pills (§8d). */
export const SheetMoreRow = ({ label = 'More nearby', onClick }) => (
  <button type="button" className={styles.ovmore} onClick={onClick}>
    {label}
    <CaretDown size={15} />
  </button>
);

/** The black commit. */
export const SheetDoneButton = ({ label = 'Done', onClick, disabled }) => (
  <button type="button" className={styles.ovdone} onClick={onClick} disabled={disabled}>
    {label}
  </button>
);

/** The second named choice under the commit; `danger` = the delete sheet's red. */
export const SheetCancelButton = ({ label = 'Cancel', onClick, danger, disabled }) => (
  <button
    type="button"
    className={[styles.ovcancel, danger && styles.danger].filter(Boolean).join(' ')}
    onClick={onClick}
    disabled={disabled}
  >
    {label}
  </button>
);

/**
 * The waiting sheet's way out (SIGNUP_Profile §3.3): a bare X, top right,
 * secondary, with a 44px tap area. Moved here from the code page (E3b-1
 * refactor row (e)).
 */
export const SheetDismissX = ({ label = 'Dismiss', onClick }) => (
  <button type="button" className={styles.dismissX} aria-label={label} onClick={onClick}>
    <X size={19} />
  </button>
);

/** The waiting sheet's spinner (.sf-spinner, 22px), under the title. */
export const SheetSpinner = ({ label = 'Waiting' }) => (
  <div className={styles.spinner} role="status" aria-label={label}>
    <span />
  </div>
);

/**
 * The arrow under a sheet's last button (share-sheet rules 5; FINALS 5B.2·b,
 * .sf-sheet-close): ph-caret-down 22px, centred, a bare control in secondary.
 */
export const SheetArrow = ({ label, onClick }) => (
  <button type="button" className={styles.arrow} aria-label={label} onClick={onClick}>
    <CaretDown size={22} />
  </button>
);

/**
 * The share sheet's head: the person it's about, a 48px round photo centred
 * above the title, 9px under it (PXP spec §3b H5, FINALS 6.2) — RoundAvatar's
 * face, so no photo shows their initial on their colour, the same size (D2).
 */
export const SheetPhotoHead = ({ photoUrl, photoMeta, name, colour }) => (
  <div className={styles.photoHead}>
    <RoundAvatar photoUrl={photoUrl} photoMeta={photoMeta} name={name} colour={colour} size={48} />
  </div>
);

/** One line of failure under the buttons. */
export const SheetError = ({ children }) =>
  children ? (
    <div className={styles.err} role="alert">
      {children}
    </div>
  ) : null;
